import type { AdminProduct } from '../../../api/group/groupAdminApiSlice';
import { describeUseCase, isRoomFamily } from '../../../utils/useCases';

/**
 * The real use cases: the LIVE entries of the use-case description
 * (`utils/useCases.ts`), which mirror the backend's `use_case_enum`.
 *
 * **`compartment_types` is not this list.** That column has also carried
 * *locker sizes* ("L"), which are not use cases at all — stated in
 * `new-backend/admin/src/lib/catalogue/productMirror.ts` and in the W1.2a
 * migration, both of which derive `use_case` only when `compartment_types`
 * holds exactly one real enum label.
 *
 * Why that matters here: a new Location's service scope is the Product's use
 * case, and the end-user location query filters it with Prisma `hasSome`. A
 * scope of `['L']` passes any non-empty check and matches nothing, so the
 * Location stays invisible in every booking flow with no error anywhere — the
 * failure looking like an absence again.
 *
 * ONE RULE, TWO COPIES — they cannot import each other (separate deployables):
 *   - here, so the wizard never offers a Product it cannot place;
 *   - `useCasesOfProduct` in `new-backend/admin/src/lib/location/locationWrite.ts`,
 *     which is authoritative and derives the scope actually written.
 * Change one and change the other.
 * Known difference: this copy reads a label case- and space-insensitively, the
 * server copy exactly. A `compartment_types` entry like `' Storage'` is offered
 * here and derives no scope there; none is known to exist (collector #2050).
 */
const isUseCase = (value: string): boolean => describeUseCase(value) !== null;

/**
 * The use case(s) a Product can actually be offered under — empty when none
 * resolves, which means the Product cannot be placed at a Location at all.
 *
 * `useCase` first (the derived, validated column), then `compartmentTypes`
 * filtered to real labels for Products backfilled before that column existed.
 */
export const resolvableUseCases = (
    product: Pick<AdminProduct, 'useCase' | 'compartmentTypes'>,
): string[] => {
    if (product.useCase && isUseCase(product.useCase)) {
        return [product.useCase.trim().toLowerCase()];
    }
    return (product.compartmentTypes ?? [])
        .filter((type) => typeof type === 'string' && isUseCase(type))
        .map((type) => type.trim().toLowerCase());
};

/*
 * Use cases a station's objects can never fulfil: the room family
 * (`isRoomFamily`). They are single-object surfaces with their own creation
 * page (Flow 1, creation-flow plan §3): the object IS the listing. Offering
 * "Villa Kunterbunt" as the product of a locker station was the staging
 * finding (PO, 2026-09-03).
 */

/**
 * The Products a station's objects may be linked to.
 *
 * ONE definition, because two surfaces now ask it: the creation wizard's link
 * step and the "add objects to an existing station" dialog. They must offer the
 * same list — a Product the wizard refuses and the add-dialog accepts would
 * scope a location to a use case no booking flow reads, with no error anywhere.
 *
 * `activeServices` is the tenant's enabled use cases. An EMPTY list means
 * unresolved, not "none enabled", so the service filter stands down rather than
 * emptying the picker (the display-level fallback in `useTenantCapabilities`).
 * The station-family exclusion holds either way.
 */
export const stationLinkableProducts = <T extends Pick<AdminProduct, 'useCase' | 'compartmentTypes'>>(
    products: readonly T[],
    activeServices: readonly string[],
): T[] => {
    const active = activeServices.map((service) => service.toLowerCase());
    return products.filter((product) => {
        if (resolvableUseCases(product).length === 0) return false;
        const types = (product.compartmentTypes ?? []).map((type) => type.toLowerCase());
        if (types.some((type) => isRoomFamily(type))) return false;
        if (active.length === 0) return true;
        return types.some((type) => active.includes(type));
    });
};

/**
 * Products bucketed by use case, sorted, for a grouped picker.
 *
 * A Product with several resolvable use cases is listed under each: it genuinely
 * is offered under all of them, and hiding that behind an arbitrary "first"
 * would be the same silence this exists to fix.
 */
export const productsByUseCase = <T extends Pick<AdminProduct, 'useCase' | 'compartmentTypes'>>(
    products: readonly T[],
): Array<[string, T[]]> => {
    const byUseCase = new Map<string, T[]>();
    for (const product of products) {
        for (const useCase of resolvableUseCases(product)) {
            byUseCase.set(useCase, [...(byUseCase.get(useCase) ?? []), product]);
        }
    }
    return Array.from(byUseCase.entries()).sort(([a], [b]) => a.localeCompare(b));
};
